import { isEqual } from "es-toolkit";
import { makeAutoObservable, runInAction, reaction, observable, toJS } from "mobx";

import {
  getTeam,
  createDocument,
  createFolder,
  updateFolder,
  disposeApi,
  updateDocument,
  importerImportFile,
  getApiErrorMessage,
  isApiError,
} from "@/api";
import { onCableMessage, onCableConnect } from "@/api/cable";
import { docPageUrl } from "@/urls";
import { isInsideMobileWebView, postMobileBridgeMessage } from "@/utils/mobileBridgeAuth";
import { ObservedBoolMap } from "@/utils/ObservedBoolMap";

import type {
  Team,
  Folder,
  Document,
  DocumentShort,
  FolderFilesItem,
  UpdateFolderBody,
  UpdateDocumentBody,
} from "@/api";
import type { RootStore } from "@/store/RootStore";
import type { LazyStore } from "@/store/types";

export function isDocument(item: Folder | DocumentShort): item is DocumentShort {
  return item.typename === "DocumentShort";
}

export function isFolder(item: Folder | DocumentShort): item is Folder {
  return item.typename === "Folder";
}

function toDocumentShort(document: Document): DocumentShort {
  return {
    typename: "DocumentShort",
    uid: document.uid,
    title: document.title,
    slug: document.slug,
    locked: document.locked,
    canToggleLock: document.canToggleLock,
    isDuplicating: document.isDuplicating,
    duplicationIncomplete: document.duplicationIncomplete,
    userPermissions: document.userPermissions,
    unreadMessagesCount: document.unreadMessagesCount,
    createdAt: document.createdAt,
  };
}

class FolderMap extends ObservedBoolMap<[folder: Folder]> {
  protected mapKey(folder: Folder): string {
    return folder.uid;
  }
}

function getImportErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    const { error: message } = (error.response?.data ?? {}) as { error?: string };
    if (message) return message;
  }
  return getApiErrorMessage(error);
}

export class DocumentTreeStore implements LazyStore {
  team?: Team;

  currentFolderId?: string;

  currentDocId?: string;

  currentPosition: number = 0;

  isDocsListExpanded = false;

  openFolders = observable.set<string>([], { deep: true });

  dndActive?: Folder | DocumentShort;

  dndHighlightFolderMap = new FolderMap();

  editFolderMap = new FolderMap();

  fileUploadPlaceholders: { parentId: string; position: number }[] = [];

  /** Attributed titles for just-duplicated docs, applied when the copy's editor mounts. */
  pendingCopyTitles = new Map<string, string>();

  uploadQueue: { file: File; parentId: string; position: number }[] = [];
  uploadError: string | undefined = undefined;

  _isProcessingUploadQueue = false;

  rootStore: RootStore;

  private loadDocumentTreeDispose?: () => void;

  private currentTeamReactionDispose?: () => void;

  private cableUnsubscribe?: () => void;

  private cableReconnectUnsubscribe?: () => void;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeAutoObservable(this);
  }

  lazyEnable() {
    this.currentTeamReactionDispose?.();
    this.currentTeamReactionDispose = reaction(
      () => this.rootStore.teams.currentTeam,
      () => {
        this.subscribe();
        this.loadDocumentTree();
      },
    );
  }

  setCurrentDocTitle(newTitle: string) {
    runInAction(() => {
      if (this.currentDoc) {
        this.currentDoc.title = newTitle;
      }
    });
  }

  setPendingCopyTitle(uid: string, title: string) {
    this.pendingCopyTitles.set(uid, title);
  }

  consumePendingCopyTitle(uid: string): string | undefined {
    const title = this.pendingCopyTitles.get(uid);
    this.pendingCopyTitles.delete(uid);
    return title;
  }

  isOpenFolder(folderOrId: Folder | string): boolean {
    const id = typeof folderOrId === "string" ? folderOrId : folderOrId.uid;
    return this.openFolders.has(id);
  }

  setCurrentDoc(docId: string, rootFolders?: FolderFilesItem[]) {
    this.currentDocId = docId;
    this.setCurrentFolder(rootFolders);
  }

  clearCurrentDoc() {
    this.currentDocId = undefined;
    this.currentFolderId = undefined;
    this.currentPosition = 0;
  }

  /** Opens an in-app document link without a full page reload. */
  openDocumentLink(docId: string) {
    // Inside the mobile WebView, hand navigation to the native host: it presents
    // the doc in the persistent warm editor (client-side nav) instead of a full
    // page load, and drives its own back stack.
    if (isInsideMobileWebView()) {
      postMobileBridgeMessage({ type: "nav:openDoc", payload: { docId } });
      return;
    }
    this.setCurrentDoc(docId);
    this.rootStore.router.push(docPageUrl({ docId }));
  }

  openFoldersByDefault() {
    if (this.root) {
      this.openFolder(this.root);
    }
    if (this.published && this.published.files?.length === 0) {
      this.openFolder(this.published);
    }
  }

  get currentDoc() {
    const item = this.itemById(this.currentDocId);
    if (item && isDocument(item)) {
      return item;
    }
    return undefined;
  }

  isCurrentDoc(doc: DocumentShort): boolean {
    if (this.dndActive) {
      return false;
    }
    return this.currentDoc?.uid === doc.uid;
  }

  get currentDocRootIndex(): number | undefined {
    const files = this.root?.files;
    const docId = this.currentDocId;
    if (!docId || !files) return undefined;

    const index = files.findIndex((item) => !!this.findFileInTree([item], docId));
    return index === -1 ? undefined : index;
  }

  expandDocsList() {
    this.isDocsListExpanded = true;
  }

  startDnd(itemId: string) {
    this.dndActive = this.itemById(itemId);
    if (this.dndActive) {
      this.expandDocsList();
    }
  }

  endDnd() {
    this.dndActive = undefined;
    this.dndHighlightFolderMap.reset();
  }

  setCurrentFolder(items?: FolderFilesItem[], parentFolder?: Folder, isTrash?: boolean) {
    this.currentPosition = 0;
    this.currentFolderId = undefined;
    if (this.currentDocId) {
      return (items || this.rootFolders).some((item, index) => {
        if (isDocument(item)) {
          if (parentFolder && this.currentDocId === item.uid) {
            if (!isTrash) {
              this.currentPosition = index + 1;
              this.currentFolderId = parentFolder.uid;
            }
            return true;
          }
        } else if (
          item.files &&
          this.setCurrentFolder(item.files, item, isTrash || this.trash?.uid === item.uid)
        ) {
          this.openFolder(item);
          return true;
        }
        return false;
      });
    }
    return false;
  }

  toggleOpenFolder(folder: Folder) {
    if (this.isOpenFolder(folder)) {
      this.closeFolder(folder);
    } else {
      this.openFolder(folder);
      if (this.root?.files && this.findFileInTree(this.root.files, folder.uid)) {
        this.expandDocsList();
      }
    }
  }

  openFolder(folderOrId: Folder | string) {
    const id = typeof folderOrId === "string" ? folderOrId : folderOrId.uid;
    this.openFolders.add(id);
  }

  closeFolder(folderOrId: Folder | string) {
    const id = typeof folderOrId === "string" ? folderOrId : folderOrId.uid;
    this.openFolders.delete(id);
  }

  /**
   * Create a new document or folder in the specified parent folder at the given position.
   *
   * Note that `position` is 1-based index.
   */
  async createDocumentOrFolder(
    type: "document" | "folder",
    parent?: Folder,
    position: number = 1,
    options: { startEditFolder?: boolean } = { startEditFolder: true },
  ): Promise<Document | Folder | undefined> {
    let parentParams;
    if (parent) {
      parentParams = { parentUid: parent.uid, position };
    } else if (this.drafts) {
      parentParams = {
        parentUid: this.drafts.uid,
        position: 1,
      };
    }
    if (!parentParams) {
      return undefined;
    }
    if (type === "document") {
      const { document } = await createDocument(parentParams);
      if (document) {
        this.addCreatedFileToTree(toDocumentShort(document), parentParams);
      }
      return document;
    }
    if (type === "folder") {
      const { folder } = await createFolder(parentParams);
      if (folder) {
        this.addCreatedFileToTree(folder, parentParams);
        if (options.startEditFolder) {
          runInAction(() => {
            this.editFolderMap.set(folder);
            this.openFolder(folder);
          });
        }
      }
      return folder;
    }
    return undefined;
  }

  private addCreatedFileToTree(
    file: FolderFilesItem,
    { parentUid, position }: { parentUid: string; position: number },
  ) {
    if (!this.itemById(file.uid)) {
      this.pushFileToTree(file, parentUid, position);
    }
  }

  async updateFolderTitle(folder: Folder, title: string) {
    folder.title = title;
    await updateFolder(folder.uid, { title }, { skipCache: true });
  }

  get rootFolders(): Folder[] {
    return [this.root, this.drafts, this.published, this.trash].filter(Boolean);
  }

  /** Documents in every root folder except the trash, including nested ones. */
  get documentsCount(): number {
    const countDocuments = (items: FolderFilesItem[] = []): number =>
      items.reduce((sum, item) => sum + (isDocument(item) ? 1 : countDocuments(item.files)), 0);
    return countDocuments([this.root, this.drafts, this.published].filter(Boolean));
  }

  isRootFolder(folder: Folder) {
    return this.rootFolders.includes(folder);
  }

  itemById(id?: string) {
    if (!id) {
      return undefined;
    }
    const fileObj = this.findFileInTree(this.rootFolders, id);
    return fileObj?.file;
  }

  setDocumentLocked(uid: string, locked: boolean) {
    const item = this.itemById(uid);
    if (item && isDocument(item)) {
      runInAction(() => {
        item.locked = locked;
      });
    }
  }

  findFileInTree(
    items: FolderFilesItem[],
    targetId: string,
    parentFolder?: Folder,
  ):
    | {
        file?: FolderFilesItem;
        fileIndex?: number;
        parentFolder?: Folder;
      }
    | undefined {
    for (const [index, item] of items.entries()) {
      if (targetId === item.uid) {
        return {
          file: item,
          fileIndex: index,
          parentFolder,
        };
      }
      if ("files" in item && item.files) {
        const child = this.findFileInTree(item.files, targetId, item);
        if (child) {
          return child;
        }
      }
    }
    return undefined;
  }

  /**
   * Upload a file to a specific folder at a specific position.
   *
   * Note that `position` is 1-based index.
   */
  async uploadFileToFolder(file: File, parentId: string, position: number) {
    this.openFolder(parentId);
    if (
      !this.fileUploadPlaceholders.find(
        (ph) => ph.parentId === parentId && ph.position === position,
      )
    ) {
      this.fileUploadPlaceholders.push({ parentId, position });
    }

    let docId: string;

    try {
      const res = await importerImportFile({
        file,
        parentUid: parentId,
        position: position.toString(),
      });
      docId = res.docId;
    } finally {
      runInAction(() => {
        this.fileUploadPlaceholders = this.fileUploadPlaceholders.filter(
          (ph) => ph.parentId !== parentId || ph.position !== position,
        );
      });
    }

    return { docId };
  }

  /**
   * Enqueue multiple files for sequential upload into a folder starting at `startPosition`.
   * Returns the docId of the first successfully uploaded file so the caller can navigate to it.
   */
  async enqueueFilesForUpload(
    files: File[],
    parentId: string,
    startPosition: number,
  ): Promise<string | undefined> {
    runInAction(() => {
      files.forEach((file, i) => {
        this.uploadQueue.push({ file, parentId, position: startPosition + i });
      });
    });

    if (this._isProcessingUploadQueue) {
      return undefined;
    }

    return this.processUploadQueue();
  }

  private async processUploadQueue(): Promise<string | undefined> {
    runInAction(() => {
      this._isProcessingUploadQueue = true;
    });

    let firstDocId: string | undefined;
    const errors: string[] = [];

    while (this.uploadQueue.length > 0) {
      const batch = this.uploadQueue;
      runInAction(() => {
        this.uploadQueue = [];
      });

      for (const item of batch) {
        try {
          // Sequential uploads are intentional: each insert shifts server-side positions,
          // so item N must complete before item N+1 is sent to get the correct ordering.
          // eslint-disable-next-line no-await-in-loop
          const { docId } = await this.uploadFileToFolder(item.file, item.parentId, item.position);
          if (!firstDocId) {
            firstDocId = docId;
          }
        } catch (error) {
          console.error("Failed to upload file:", item.file.name, error);
          errors.push(`${item.file.name}: ${getImportErrorMessage(error)}`);
        }
      }
    }

    runInAction(() => {
      this._isProcessingUploadQueue = false;
      if (errors.length > 0) {
        this.uploadError = [this.uploadError, ...errors].filter(Boolean).join("\n");
      }
    });

    return firstDocId;
  }

  dismissUploadError() {
    this.uploadError = undefined;
  }

  resetPlaceholdersInFolder(folderId: string) {
    this.fileUploadPlaceholders = this.fileUploadPlaceholders.filter(
      (ph) => ph.parentId !== folderId,
    );
  }

  /**
   * Update file position in the tree and backend.
   *
   * Note that `position` is 1-based index.
   */
  async updateFilePosition(targetId: string, newFolderId: string, newPosition: number) {
    if (!this.root) {
      return;
    }
    const targetFile = this.itemById(targetId);
    if (!targetFile) {
      return;
    }

    const position = Math.max(1, newPosition);

    this.moveFileInTree(targetFile, newFolderId, position);

    const fileNewBody: UpdateDocumentBody | UpdateFolderBody = {
      title: targetFile.title || "",
      parentUid: newFolderId,
      position,
    };

    if (isFolder(targetFile)) {
      await updateFolder(targetId, fileNewBody);
    } else {
      await updateDocument(targetId, fileNewBody);
    }
  }

  removeFile(id: string) {
    return (
      this.removeFileFromTree(this.root, id) ||
      this.removeFileFromTree(this.drafts, id) ||
      this.removeFileFromTree(this.published, id) ||
      this.removeFileFromTree(this.trash, id)
    );
  }

  removeFileFromTree(tree: Folder | undefined = undefined, targetId: string): boolean {
    if (!tree) {
      return false;
    }
    const treeHasDraggedFile = tree.files?.find(({ uid }) => {
      return uid === targetId;
    });
    if (treeHasDraggedFile) {
      tree.files = tree.files?.filter(({ uid }) => {
        return uid !== targetId;
      });
      return true;
    }
    const folders = (tree.files || []).filter((item) => "files" in item) as Folder[];
    for (const folder of folders) {
      const result = this.removeFileFromTree(folder, targetId);
      if (result) {
        return true;
      }
    }
    return false;
  }

  private pushFileToTree(
    file: FolderFilesItem,
    newFolderId: string,
    newPosition: number = 1,
  ): boolean {
    const targetFolder = this.itemById(newFolderId);
    if (targetFolder && !isDocument(targetFolder)) {
      const fileToAdd = isFolder(file)
        ? { ...file, files: file.files ? [...file.files] : [] }
        : { ...file };
      targetFolder.files?.splice(Math.max(newPosition, 1) - 1, 0, fileToAdd);
      return true;
    }
    return false;
  }

  private moveFileInTree(file: FolderFilesItem, newFolderId: string, newPosition: number) {
    const childFiles = isFolder(file) ? [...(file.files || [])] : [];
    this.removeFile(file.uid);
    if (childFiles && isFolder(file)) {
      file.files = childFiles;
    }
    this.pushFileToTree(file, newFolderId, newPosition);
  }

  get currentTeam() {
    return this.rootStore.teams.currentTeam;
  }

  loadDocumentTree({ openDefaultFolders = true }: { openDefaultFolders?: boolean } = {}) {
    this.loadDocumentTreeDispose?.();
    this.loadDocumentTreeDispose = disposeApi(async (signal) => {
      if (this.currentTeam) {
        const getTeamResult = await getTeam(this.currentTeam.permalink, { signal });
        if (getTeamResult.team) {
          if (!this.isSameTeamData(getTeamResult.team)) {
            this.setCurrentTeam(getTeamResult.team);
          }
          if (openDefaultFolders) {
            this.openFoldersByDefault();
          }
        }
      }
    });
  }

  private isSameTeamData(team: Team): boolean {
    return this.team?.uid === team.uid && isEqual(toJS(this.team), team);
  }

  setCurrentTeam(team: Team) {
    this.team = team;
    this.setCurrentFolder();
  }

  private subscribe() {
    this.cableUnsubscribe?.();
    this.cableReconnectUnsubscribe?.();

    this.cableUnsubscribe = onCableMessage((payload) => {
      if (payload.type === "team_updated" && payload.team_branch) {
        // In classroom teams the whole-team stream carries every member's
        // drafts, which the server hides from this user's tree; merging the
        // payload would surface them. Refetch the server-filtered tree
        // instead (docs/plans/student-permissions.md).
        if (this.rootStore.classroom.isClassroomTeam(this.team?.uid)) {
          this.resetPlaceholdersInFolder(payload.team_branch.parent.uid);
          this.loadDocumentTree();
          return;
        }

        const matchedFile = this.itemById(payload.team_branch.branch.uid);
        if (matchedFile) {
          const newFile = { ...payload.team_branch.branch };
          if (isFolder(newFile) && isFolder(matchedFile)) {
            newFile.files = [...(matchedFile.files || [])];
          }
          this.moveFileInTree(
            newFile,
            payload.team_branch.parent.uid,
            payload.team_branch.position,
          );
        } else {
          this.pushFileToTree(
            payload.team_branch.branch,
            payload.team_branch.parent.uid,
            payload.team_branch.position,
          );
        }

        // this is not ideal as if there are multiple uploads in the same folder,
        // they will all be removed when one upload completes.
        this.resetPlaceholdersInFolder(payload.team_branch.parent.uid);
      }
    });
    this.cableReconnectUnsubscribe = onCableConnect((e) => {
      if (e.reconnect) {
        this.loadDocumentTree({ openDefaultFolders: false });
      }
    });
  }

  getParentFolder(fileOrId: FolderFilesItem | string): { folder?: Folder; currentIndex?: number } {
    const id = typeof fileOrId === "string" ? fileOrId : fileOrId.uid;
    const fileObj = this.findFileInTree(this.rootFolders, id);
    if (fileObj?.parentFolder) {
      return {
        folder: fileObj.parentFolder,
        currentIndex: fileObj.fileIndex ?? 0,
      };
    }
    return {};
  }

  getAdjacentDocument(file: FolderFilesItem): DocumentShort | undefined {
    const { folder: parentFolder, currentIndex = 0 } = this.getParentFolder(file);
    const prevDocumentInCurrentFolder = parentFolder?.files
      ?.slice(0, currentIndex)
      .findLast((f) => f.typename === "DocumentShort");
    if (prevDocumentInCurrentFolder) {
      return prevDocumentInCurrentFolder;
    }
    const nextDocumentInCurrentFolder = parentFolder?.files
      ?.slice(currentIndex + 1)
      .find((f) => f.typename === "DocumentShort");
    if (nextDocumentInCurrentFolder) {
      return nextDocumentInCurrentFolder;
    }
    if (parentFolder) {
      return this.getAdjacentDocument(parentFolder);
    }
    return undefined;
  }

  getTargetFolderAndIndex(): { folder: Folder; index: number } | undefined {
    let targetFolder;
    let targetIndex = 0;

    if (this.currentDocId) {
      const { folder, currentIndex } = this.getParentFolder(this.currentDocId);
      targetFolder = folder;
      targetIndex = currentIndex !== undefined ? currentIndex + 2 : 0;
    }
    if (this.root && !targetFolder) {
      targetFolder = this.root;
    }

    if (!targetFolder) {
      return undefined;
    }

    return { folder: targetFolder, index: targetIndex };
  }

  get root() {
    return this.team?.root;
  }

  get trash() {
    return this.team?.trash;
  }

  isInTrash(id: string) {
    return !!this.trash?.files && !!this.findFileInTree(this.trash.files, id);
  }

  get drafts() {
    return this.team?.drafts;
  }

  get published() {
    return this.team?.published;
  }

  dispose() {
    this.currentTeamReactionDispose?.();
    this.loadDocumentTreeDispose?.();
    this.cableUnsubscribe?.();
    this.cableReconnectUnsubscribe?.();
  }
}
